import { Box } from '@mui/material';
import { FC } from 'react';

import { SelectStyled } from '../TicketsSorting/TicketsSorting.styled';
import { SubCategoriesFilter } from './components/SubCategoriesFilter/SubCategoriesFilter';
import useTicketsRefinement from './hooks/useTicketsRefinement';
import {
  FilterBtnStyled,
  MobileFilterBtnStyled,
  MobileFiltersFooterStyled,
  MobileFiltersRowWrapStyled,
  MobileFiltersWrapStyled,
  MobileFooterBtnStyled,
  MobileSortingBtnStyled,
  TitleStyled,
  popupContentStyle,
} from './TicketsRefinement.styled';
import Button from 'components/Button/Button';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import { DropdownCheckboxList } from 'components/MUICheckboxList/CheckboxList';
import Popup from 'components/Popup/Popup';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { SortingOrderE } from 'declarations/enums/common';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import {
  AgeFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import { TuneIcon } from 'icons';

const TicketsRefinement: FC = () => {
  const { isMobile } = useResponsive();
  const { ticketsCount, isLoadingTickets } = useTickets();
  const {
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
    isFiltersPopupOpened,
    isAscSortingActive,
    isDescSortingActive,
    sortOptions,
    sortSelectValue,
    handleUpdateSortSelectClick,
    handleUpdateFilterClick,
    handleClearAllFiltersClick,
    handleOrderSortSelectClick,
    handleOpenFiltersPopup,
    handleCloseFiltersPopup,
    filtersReset,
    isScheduleSelected,
    setIsScheduleSelected,
  } = useTicketsRefinement();
  const { activeScheduleFilters, selectedRoofTop } = useTickets();

  if (isMobile) {
    return (
      <FlexContainer justify="flex-end">
        <Button
          size="xl"
          variant="outline"
          icon={<TuneIcon size="2xl" />}
          onClick={handleOpenFiltersPopup}
        >
          Filter&Sort
        </Button>

        <Popup
          showCloseBtn
          popupId={ScrollLockIdE.FiltersPopup}
          contentStyle={popupContentStyle}
          isOpen={isFiltersPopupOpened}
          onClose={handleCloseFiltersPopup}
          closeOnEscape={false}
        >
          <TitleStyled align="center">Filter and Sort</TitleStyled>
          <MobileFiltersWrapStyled>
            <FlexContainer>
              <SelectStyled
                onChange={handleUpdateSortSelectClick}
                placeholder="Sort by:"
                options={sortOptions}
                value={sortSelectValue}
              />
              <MobileSortingBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isAscSortingActive}
                onClick={handleOrderSortSelectClick(
                  TicketsRefinementKeysE.Order,
                  SortingOrderE.Asc
                )}
              >
                Ascending
              </MobileSortingBtnStyled>
              <MobileSortingBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isDescSortingActive}
                onClick={handleOrderSortSelectClick(
                  TicketsRefinementKeysE.Order,
                  SortingOrderE.Desc
                )}
              >
                Descending
              </MobileSortingBtnStyled>
            </FlexContainer>
            <MobileFiltersRowWrapStyled>
              <Typography variant="h5">Ticket status</Typography>
              <MobileFilterBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isOpenStatusFilterActive}
                onClick={handleUpdateFilterClick(
                  TicketsRefinementKeysE.Status,
                  StatusFilterValuesE.Open
                )}
              >
                Open
              </MobileFilterBtnStyled>
              <MobileFilterBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isCloseStatusFilterActive}
                onClick={handleUpdateFilterClick(
                  TicketsRefinementKeysE.Status,
                  StatusFilterValuesE.Closed
                )}
              >
                Closed
              </MobileFilterBtnStyled>
            </MobileFiltersRowWrapStyled>
            <MobileFiltersRowWrapStyled>
              <Typography variant="h5">Ticket age</Typography>
              <MobileFilterBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isUnder24HoursFilterActive}
                onClick={handleUpdateFilterClick(
                  TicketsRefinementKeysE.TicketAge,
                  AgeFilterValuesE.Under24Hours
                )}
              >
                Under 24h
              </MobileFilterBtnStyled>
              <MobileFilterBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isOver24HoursFilterActive}
                onClick={handleUpdateFilterClick(
                  TicketsRefinementKeysE.TicketAge,
                  AgeFilterValuesE.Over24Hours
                )}
              >
                Over 24h
              </MobileFilterBtnStyled>
              <MobileFilterBtnStyled
                isMultiFilter
                size="xl"
                variant="outline"
                isActive={isPast30DaysFilterActive}
                onClick={handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true')}
              >
                Past 30 Days
              </MobileFilterBtnStyled>
            </MobileFiltersRowWrapStyled>
            <MobileFiltersRowWrapStyled>
              <MobileFilterBtnStyled
                isMultiFilter
                size="md"
                variant="outline"
                isActive={isPastMonthFilterActive}
                onClick={handleUpdateFilterClick(TicketsRefinementKeysE.PastMonth, 'true')}
              >
                Past month
              </MobileFilterBtnStyled>
              <MobileFilterBtnStyled
                isMultiFilter
                size="md"
                variant="outline"
                isActive={isArchivedFilterActive}
                onClick={handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true')}
              >
                Archived
              </MobileFilterBtnStyled>
            </MobileFiltersRowWrapStyled>
            <MobileFiltersRowWrapStyled>
              <Typography variant="h5">Ticket assignee</Typography>
              <MobileFilterBtnStyled
                size="xl"
                variant="outline"
                isActive={isAssignedToMeFilterActive}
                onClick={handleUpdateFilterClick(TicketsRefinementKeysE.AssignedToMe, 'true')}
              >
                My Tickets
              </MobileFilterBtnStyled>
            </MobileFiltersRowWrapStyled>
            <MobileFiltersFooterStyled>
              <MobileFooterBtnStyled
                size="xl"
                variant="outline"
                onClick={handleClearAllFiltersClick}
              >
                Clear Filters
              </MobileFooterBtnStyled>
              <MobileFooterBtnStyled
                size="xl"
                isLoading={isLoadingTickets}
                onClick={handleCloseFiltersPopup}
              >
                Show {ticketsCount} tickets
              </MobileFooterBtnStyled>
            </MobileFiltersFooterStyled>
          </MobileFiltersWrapStyled>
        </Popup>
      </FlexContainer>
    );
  }

  return (
    <FlexContainer>
      <FlexContainer className="filters">
        <Typography variant="body1" color="darkBlue" family="secondary">
          Filter By:
        </Typography>
        <Box marginLeft="12px">
          <DropdownCheckboxList
            shouldFiltersReset={filtersReset}
            onScheduleSelect={setIsScheduleSelected}
          />
        </Box>
        {(isScheduleSelected || activeScheduleFilters.length) && selectedRoofTop ? (
          <Box marginLeft="12px">
            <SubCategoriesFilter
              shouldFiltersReset={filtersReset}
              isScheduleSelected={isScheduleSelected}
            />
          </Box>
        ) : null}
        <FilterBtnStyled
          title="Shows tickets assigned to you (for primary dealers)"
          size="sm"
          variant="outline-rounded"
          isActive={isAssignedToMeFilterActive}
          onClick={handleUpdateFilterClick(TicketsRefinementKeysE.AssignedToMe, 'true')}
        >
          My Tickets
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows open and reopen tickets"
          size="sm"
          variant="outline-rounded"
          isActive={isOpenStatusFilterActive}
          onClick={handleUpdateFilterClick(TicketsRefinementKeysE.Status, StatusFilterValuesE.Open)}
        >
          Open
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows closed tickets"
          size="sm"
          variant="outline-rounded"
          isActive={isCloseStatusFilterActive}
          onClick={handleUpdateFilterClick(
            TicketsRefinementKeysE.Status,
            StatusFilterValuesE.Closed
          )}
        >
          Closed
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows tickets created less than 24h ago"
          size="sm"
          variant="outline-rounded"
          isActive={isUnder24HoursFilterActive}
          onClick={handleUpdateFilterClick(
            TicketsRefinementKeysE.TicketAge,
            AgeFilterValuesE.Under24Hours
          )}
        >
          Under 24h
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows tickets created more than 24h ago"
          size="sm"
          variant="outline-rounded"
          isActive={isOver24HoursFilterActive}
          onClick={handleUpdateFilterClick(
            TicketsRefinementKeysE.TicketAge,
            AgeFilterValuesE.Over24Hours
          )}
        >
          Over 24h
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows tickets created in the last 30 days"
          size="sm"
          variant="outline-rounded"
          isActive={isPast30DaysFilterActive}
          onClick={handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true')}
        >
          Past 30 Days
        </FilterBtnStyled>
        <FilterBtnStyled
          title="Shows closed tickets created more than 60 days ago"
          size="sm"
          variant="outline-rounded"
          isActive={isArchivedFilterActive}
          onClick={handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true')}
        >
          Archived
        </FilterBtnStyled>
        <FilterBtnStyled
          size="sm"
          variant="outline-rounded"
          onClick={handleClearAllFiltersClick}
          title="Reset all filters and sorting"
        >
          Clear Filters
        </FilterBtnStyled>
      </FlexContainer>
    </FlexContainer>
  );
};

export default TicketsRefinement;
